import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { format, subDays, parseISO } from "date-fns";
import { useAuth } from "@/lib/authContext";
import { api, type TeamTimeEntry, type AdminUser, type PayPeriod } from "@/lib/api";
import { Layout } from "@/components/layout/Layout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Users, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

// Compute current pay period date range based on immutable pay period logic:
// Period A: 25th of prior month – 8th of current month
// Period B: 9th – 24th of current month
function getCurrentPayPeriodRange(): { startDate: string; endDate: string; label: string } {
  const today = new Date();
  const day = today.getDate();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-indexed

  if (day >= 9 && day <= 24) {
    // Period B: 9th–24th of this month
    const start = new Date(year, month, 9);
    const end = new Date(year, month, 24);
    return {
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
      label: `Period B (${format(start, "MMM d")}–${format(end, "MMM d, yyyy")})`,
    };
  } else {
    // Period A: 25th of prior month – 8th of current month
    const start = day >= 25
      ? new Date(year, month, 25)
      : new Date(year, month - 1, 25);
    const end = day >= 25
      ? new Date(year, month + 1, 8)
      : new Date(year, month, 8);
    return {
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
      label: `Period A (${format(start, "MMM d")}–${format(end, "MMM d, yyyy")})`,
    };
  }
}

function getPreviousPayPeriodRange(current: { startDate: string; endDate: string }): { startDate: string; endDate: string; label: string } {
  // Go back 1 day from current start to land in the previous period, then compute
  const dayBefore = subDays(parseISO(current.startDate), 1);
  const day = dayBefore.getDate();
  const year = dayBefore.getFullYear();
  const month = dayBefore.getMonth();

  if (day >= 9 && day <= 24) {
    const start = new Date(year, month, 9);
    const end = new Date(year, month, 24);
    return {
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
      label: `Period B (${format(start, "MMM d")}–${format(end, "MMM d, yyyy")})`,
    };
  } else {
    const start = day >= 25
      ? new Date(year, month, 25)
      : new Date(year, month - 1, 25);
    const end = day >= 25
      ? new Date(year, month + 1, 8)
      : new Date(year, month, 8);
    return {
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
      label: `Period A (${format(start, "MMM d")}–${format(end, "MMM d, yyyy")})`,
    };
  }
}

interface StaffRow {
  userId: string;
  name: string;
  role: string;
  totalHours: number;
  entryCount: number;
}

export default function AllTimesheets() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // Role guard
  if (user && (user.role === "staff" || user.role === "contractor")) {
    setLocation("/");
    return null;
  }

  const currentPeriod = getCurrentPayPeriodRange();
  const prevPeriod = getPreviousPayPeriodRange(currentPeriod);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string; label: string }>(currentPeriod);
  const [selectedUserId, setSelectedUserId] = useState<string>("all");

  // Fetch all admin users for the dropdown
  const { data: adminUsers = [], isLoading: usersLoading } = useQuery<AdminUser[]>({
    queryKey: ["admin-users"],
    queryFn: () => api.getAdminUsers(),
    enabled: !!user,
  });

  // Fetch team time entries for the selected range
  const { data: teamEntries = [], isLoading: entriesLoading } = useQuery<TeamTimeEntry[]>({
    queryKey: ["team-time-entries", dateRange.startDate, dateRange.endDate, selectedUserId],
    queryFn: () =>
      api.getTeamTimeEntries({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...(selectedUserId !== "all" ? { userId: selectedUserId } : {}),
      }),
    enabled: !!user,
  });

  // Group entries by user and sum hours
  const staffRows: StaffRow[] = (() => {
    const map = new Map<string, StaffRow>();

    // Seed with all non-client users so zero-entry users still appear
    const relevantUsers = adminUsers.filter(
      (u) => u.is_active && u.role !== "client"
    );
    for (const u of relevantUsers) {
      if (selectedUserId !== "all" && u.id !== selectedUserId) continue;
      map.set(u.id, {
        userId: u.id,
        name: u.name,
        role: u.role,
        totalHours: 0,
        entryCount: 0,
      });
    }

    // Sum hours from entries
    for (const entry of teamEntries) {
      if (!map.has(entry.user_id)) {
        // Entry for a user not in admin list (e.g. inactive) — still show
        map.set(entry.user_id, {
          userId: entry.user_id,
          name: entry.user_name || "Unknown",
          role: "staff",
          totalHours: 0,
          entryCount: 0,
        });
      }
      const row = map.get(entry.user_id)!;
      row.totalHours += Number(entry.hours) || 0;
      row.entryCount += 1;
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const isLoading = usersLoading || entriesLoading;

  const roleBadgeVariant = (role: string): "default" | "secondary" | "outline" => {
    if (role === "admin") return "default";
    if (role === "manager") return "secondary";
    return "outline";
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-7 h-7 text-slate-500" />
            <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-slate-100">
              All Staff Timesheets
            </h1>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Period navigation */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => setDateRange(prevPeriod)}
              title="Previous period"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium px-2 text-slate-700 dark:text-slate-300 min-w-[220px] text-center">
              {dateRange.label}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => setDateRange(currentPeriod)}
              title="Current period"
              disabled={dateRange.startDate === currentPeriod.startDate}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Jump to current */}
          {dateRange.startDate !== currentPeriod.startDate && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDateRange(currentPeriod)}
            >
              Current Period
            </Button>
          )}

          {/* User filter */}
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger className="w-48 h-9">
              <SelectValue placeholder="All staff" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              {adminUsers
                .filter((u) => u.is_active && u.role !== "client")
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary stat */}
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Clock className="w-4 h-4" />
          <span>
            {isLoading
              ? "Loading..."
              : `${staffRows.length} staff member${staffRows.length !== 1 ? "s" : ""} — ${staffRows.reduce((s, r) => s + r.totalHours, 0).toFixed(1)} total hours logged`}
          </span>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Loading timesheets…
              </div>
            ) : staffRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                <Users className="w-8 h-8 mb-2 opacity-40" />
                <p className="text-sm">No staff members found.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead className="pl-4">Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Hours Logged</TableHead>
                    <TableHead className="text-right">Entries</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffRows.map((row) => (
                    <TableRow
                      key={row.userId}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40"
                      onClick={() => setLocation("/period")}
                      title="Navigate to Timesheet page"
                    >
                      <TableCell className="pl-4 font-medium text-slate-900 dark:text-slate-100">
                        {row.name}
                      </TableCell>
                      <TableCell>
                        <Badge variant={roleBadgeVariant(row.role)} className="capitalize text-xs">
                          {row.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-mono text-slate-700 dark:text-slate-300">
                        {row.totalHours.toFixed(1)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-500 dark:text-slate-400">
                        {row.entryCount}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.entryCount > 0 ? (
                          <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-700 text-xs">
                            Submitted
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-400 border-slate-300 text-xs">
                            No entries
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right pr-4">
                        <span className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                          View →
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
